import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useOwners, useDeleteOwner } from '@/hooks/useOwners';
import { Owner } from '@/types';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { Plus, Search, Edit, Trash2, User, Phone, Mail, MapPin, Eye, Dog } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import OwnerForm from '@/components/OwnerForm';
import OwnerWithPatientForm from '@/components/OwnerWithPatientForm';
import PatientForm from '@/components/PatientForm';

export default function Owners() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Owner | null>(null);
  const [addingPetToOwner, setAddingPetToOwner] = useState<Owner | null>(null);

  const { data: ownersData, isLoading } = useOwners(search || undefined);
  const owners = useMemo(() => {
    if (!ownersData) return ownersData;
    return [...ownersData].sort((a, b) => {
      const aFull = `${a.first_name} ${a.last_name}`.toLowerCase();
      const bFull = `${b.first_name} ${b.last_name}`.toLowerCase();
      return aFull.localeCompare(bFull);
    });
  }, [ownersData]);
  const deleteOwner = useDeleteOwner();

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteOwner.mutateAsync(deleteConfirm.id);
      toast.success('Cliente eliminado');
      setDeleteConfirm(null);
    } catch (error: any) {
      console.error('Error deleting owner:', error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : 'No se pudo eliminar el cliente. Intentá de nuevo.');
    }
  };

  const getFullName = (owner: Owner) => `${owner.first_name} ${owner.last_name}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Propietarios</h1>
          <p className="text-gray-600">Gestiona los dueños de mascotas</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Propietario
        </Button>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre, teléfono o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Lista de propietarios */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : owners && owners.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Propietario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Contacto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Mascotas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {owners.map((owner) => (
                  <tr key={owner.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/dashboard/owners/${owner.id}`)}
                        className="flex items-center text-left hover:bg-gray-100 rounded-lg p-1 -m-1 transition-colors"
                      >
                        <div className="flex-shrink-0 h-10 w-10 bg-taupe-100 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-taupe-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 hover:text-taupe-700">
                            {getFullName(owner)}
                          </div>
                          {owner.address && (
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {owner.address.length > 30
                                ? `${owner.address.substring(0, 30)}...`
                                : owner.address}
                            </div>
                          )}
                        </div>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {owner.phone && (
                          <div className="text-sm text-gray-900 flex items-center gap-1">
                            <Phone className="h-3 w-3 text-gray-400" />
                            {owner.phone}
                          </div>
                        )}
                        {owner.email && (
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <Mail className="h-3 w-3 text-gray-400" />
                            {owner.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/dashboard/owners/${owner.id}`)}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cream-100 text-cream-800 hover:bg-cream-200 transition-colors cursor-pointer"
                      >
                        {owner.patients?.length || 0} mascotas
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAddingPetToOwner(owner)}
                          title="Agregar mascota"
                          className="text-cream-700 hover:text-cream-800 hover:bg-cream-50"
                        >
                          <Dog className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/dashboard/owners/${owner.id}`)}
                          title="Ver detalle"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingOwner(owner);
                            setIsModalOpen(true);
                          }}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(owner)}
                          title="Eliminar"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No se encontraron propietarios' : 'No hay propietarios registrados'}
          </div>
        )}
      </div>

      {/* Modal de propietario */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingOwner(null);
        }}
        title={editingOwner ? 'Editar Propietario' : 'Nuevo Propietario'}
        size="lg"
      >
        {editingOwner ? (
          <OwnerForm
            owner={editingOwner}
            onSuccess={() => {
              setIsModalOpen(false);
              setEditingOwner(null);
            }}
          />
        ) : (
          <OwnerWithPatientForm
            onSuccess={() => {
              setIsModalOpen(false);
            }}
          />
        )}
      </Modal>

      {/* Modal de agregar mascota */}
      <Modal
        isOpen={!!addingPetToOwner}
        onClose={() => setAddingPetToOwner(null)}
        title={`Agregar mascota a ${addingPetToOwner?.first_name} ${addingPetToOwner?.last_name}`}
        size="lg"
      >
        {addingPetToOwner && (
          <PatientForm
            ownerId={addingPetToOwner.id}
            onSuccess={() => setAddingPetToOwner(null)}
          />
        )}
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Eliminar Propietario"
        size="sm"
      >
        {deleteConfirm && (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de eliminar a <strong>{getFullName(deleteConfirm)}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Esta acción eliminará también todas las mascotas asociadas y no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                loading={deleteOwner.isPending}
              >
                Eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
